import { useMemo, useRef } from 'react'

import { BookingSearchBar } from '@hhgtech/hhg-components/care'
import {
  CARE_LOCALES,
  ECOM_LOCALES,
  LOCALE_HAS_COMMUNITY_SITE,
} from '@hhgtech/hhg-components/constants'
import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import {
  Text,
  Button,
  Indicator,
  Carousel,
} from '@hhgtech/hhg-components/mantine'
import {
  AspectRatio,
  Box,
  clsx,
  Container,
  Flex,
  MediaQuery,
  Stack,
} from '@mantine/core'
import Autoplay from 'embla-carousel-autoplay'
import Head from 'next/head'
import { useIntl } from 'react-intl'

import { NextImageWrap } from 'components/atoms/imageWrap'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  ENABLE_CARE_MENU_LOCALES,
  ENV,
  assetImagePath,
  basePath,
} from 'interfaces/constants'
import { HomepageBannerItemType } from 'interfaces/types'

import {
  StyledBannerMenu,
  StyledBannerMenuWrapper,
  StyledHomepageBannerWrapper,
  StyledHomepageContainer,
  StyledHomepageImg,
  StyledMenuCard,
  StyledMenuDivider,
  StyledMenuIcon,
  StyledMenuText,
} from './index.styled'

type HomepageBannerProps = {
  isMobile: boolean
  autoplayTime?: number
  banners: HomepageBannerItemType[]
  title: string
  description: string
  image: string
  type: string
  ctaUrl: string
  ctaText: string
  openNewTab?: boolean
}

type HomepageBannerMenuProps = {
  hasDivider?: boolean
}
const HomepageBannerMenu = ({
  hasDivider = false,
}: HomepageBannerMenuProps) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()
  const itemCount = useMemo(() => {
    let count = 2
    if (ENABLE_CARE_MENU_LOCALES.includes(locale)) {
      count += 1
    }
    if (LOCALE_HAS_COMMUNITY_SITE.includes(locale)) {
      count += 1
    }
    if (ECOM_LOCALES.includes(locale)) {
      count += 1
    }
    return count
  }, [locale])

  return (
    <StyledBannerMenu className="homepageBannerMenu">
      <StyledBannerMenuWrapper className="menu-wrapper">
        <StyledMenuCard
          href="/categories"
          data-event-category="Home Page Nav"
          data-event-action="Click"
          data-event-label={`https://${domainLocales[locale]}${basePath}/categories/`}
          data-two-column={itemCount % 2 === 1}
        >
          <StyledMenuIcon
            src={CommonGAssets.getAssetPath('nav-icons/discover.svg')}
          />
          <StyledMenuText
            size={isMobile ? 'n1' : 's3'}
            weight={isMobile ? 'regular' : 'semiBold'}
          >
            {f({
              id: 'home.navigator.menuItem.discover',
              defaultMessage: 'Discover',
            })}
          </StyledMenuText>
        </StyledMenuCard>
        {ENABLE_CARE_MENU_LOCALES.includes(locale) && (
          <StyledMenuCard
            href="/care"
            data-event-category="Home Page Nav"
            data-event-action="Click"
            data-event-label={`https://${domainLocales[locale]}${basePath}/care/`}
          >
            <StyledMenuIcon
              src={CommonGAssets.getAssetPath('nav-icons/care.svg')}
            />
            <StyledMenuText
              size={isMobile ? 'n1' : 's3'}
              weight={isMobile ? 'regular' : 'semiBold'}
            >
              {f({
                id: 'home.navigator.menuItem.helloCare',
                defaultMessage: 'Book Doctor',
              })}
            </StyledMenuText>
          </StyledMenuCard>
        )}
        <StyledMenuCard
          href={`https://${domainLocales[locale]}${basePath}/health-tools/`}
          data-event-category="Home Page Nav"
          data-event-action="Click"
          data-event-label={`https://${domainLocales[locale]}${basePath}/health-tools/`}
        >
          <StyledMenuIcon
            src={CommonGAssets.getAssetPath('nav-icons/health-tools.svg')}
          />
          <StyledMenuText
            size={isMobile ? 'n1' : 's3'}
            weight={isMobile ? 'regular' : 'semiBold'}
          >
            {f({
              id: 'home.navigator.menuItem.healthTools',
              defaultMessage: 'Health Tools',
            })}
          </StyledMenuText>
        </StyledMenuCard>
        {ECOM_LOCALES.includes(locale) && (
          <StyledMenuCard
            href={`https://${domainLocales[locale]}${basePath}/shop/`}
            data-event-category="Home Page Nav"
            data-event-action="Click"
            data-event-label={`https://${domainLocales[locale]}${basePath}/shop/`}
          >
            <Indicator
              disabled={!isMobile}
              color="green"
              label={f({ id: 'common.new' })}
              size={20}
              styles={{
                indicator: {
                  transform: `translate(100%, -50%) !important`,
                  top: '0 !important',
                  right: '0 !important',
                },
              }}
            >
              <StyledMenuIcon
                src={CommonGAssets.getAssetPath('nav-icons/shop.svg')}
              />
            </Indicator>
            <Indicator
              disabled={isMobile}
              color="green"
              label={f({ id: 'common.new' })}
              size={20}
              styles={{
                root: {
                  paddingRight: isMobile ? 0 : 20,
                },
              }}
            >
              <StyledMenuText
                size={isMobile ? 'n1' : 's3'}
                weight={isMobile ? 'regular' : 'semiBold'}
              >
                {f({
                  id: 'home.navigator.menuItem.shop',
                  defaultMessage: 'Shop',
                })}
              </StyledMenuText>
            </Indicator>
          </StyledMenuCard>
        )}
        {LOCALE_HAS_COMMUNITY_SITE.includes(locale) && (
          <StyledMenuCard
            href="/community"
            data-event-category="Home Page Nav"
            data-event-action="Click"
            data-event-label={`https://${domainLocales[locale]}/community/`}
          >
            <StyledMenuIcon
              src={CommonGAssets.getAssetPath('nav-icons/community.svg')}
            />
            <StyledMenuText
              size={isMobile ? 'n1' : 's3'}
              weight={isMobile ? 'regular' : 'semiBold'}
            >
              {f({
                id: 'home.navigator.menuItem.helloTogether',
                defaultMessage: 'Communities',
              })}
            </StyledMenuText>
          </StyledMenuCard>
        )}
      </StyledBannerMenuWrapper>
      {hasDivider && <StyledMenuDivider />}
    </StyledBannerMenu>
  )
}

const HomepageBanner = ({
  autoplayTime = 0,
  banners = [],
  title,
  description,
  image,
  isMobile,
  type,
  ctaUrl,
  ctaText,
}: HomepageBannerProps) => {
  const { locale } = useRouterExtend()
  const autoplay = useRef(
    Autoplay({
      delay: Number(autoplayTime) * 1000,
      stopOnInteraction: false,
    }),
  )
  return (
    <>
      <Head>
        <link
          rel="preload"
          href={`${assetImagePath}/homepage-banner/banner-bg.svg`}
          as="image"
          // @ts-ignore
          fetchPriority="high"
        />
        {!!image && (
          <link
            rel="preload"
            href={image}
            as="image"
            // @ts-ignore
            fetchPriority="high"
          />
        )}
      </Head>
      <StyledHomepageBannerWrapper
        className={clsx({ 'only-menu': !title }, 'homepageBanner')}
        style={{
          backgroundImage: isMobile
            ? `url("${assetImagePath}/homepage-banner/banner-bg.svg")`
            : 'unset',
        }}
      >
        {title || type === 'upload' ? (
          <>
            {type === 'upload' ? (
              <Carousel
                withControls={false}
                withIndicators={banners.length > 1}
                loop
                plugins={autoplayTime > 0 ? [autoplay.current] : []}
                styles={(theme) => ({
                  indicator: {
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    border: `1px solid ${theme.colors.neutral100}`,
                    boxShadow: 'none',
                    background: '#fff',
                    opacity: 1,
                    '&[data-active]': {
                      borderColor: theme.fn.primaryColor(),
                      background: theme.fn.primaryColor(),
                    },
                  },
                })}
              >
                {banners.map((i, idx) => (
                  <Carousel.Slide key={idx}>
                    <Box
                      component={i.url ? 'a' : 'div'}
                      href={i.url}
                      target={i.openNewTab ? '_blank' : '_self'}
                      {...(i.url && {
                        'data-event-category': 'Slicing Banners',
                        'data-event-action': 'Banner Click',
                        'data-event-label': i.url,
                      })}
                    >
                      <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
                        <AspectRatio ratio={1920 / 416} key={idx}>
                          <NextImageWrap
                            src={i.desktop}
                            layout="fill"
                            alt={''}
                            quality={100}
                          />
                        </AspectRatio>
                      </MediaQuery>
                      <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
                        <AspectRatio ratio={375 / 220} key={idx}>
                          <NextImageWrap
                            src={i.mobile}
                            layout="fill"
                            alt={''}
                            quality={100}
                          />
                        </AspectRatio>
                      </MediaQuery>
                    </Box>
                  </Carousel.Slide>
                ))}
              </Carousel>
            ) : (
              <StyledHomepageContainer
                style={{
                  backgroundImage: !isMobile
                    ? `url("${assetImagePath}/homepage-banner/banner-bg.svg")`
                    : 'unset',
                }}
              >
                <Container size={'lg'} pt={{ lg: 24 }}>
                  <Flex
                    justify={'space-between'}
                    align={'center'}
                    h={'100%'}
                    gap={0}
                  >
                    <Stack spacing={16} maw={600} sx={{ flexGrow: 1 }}>
                      <Text as="h1" size={isMobile ? 'h3' : 'h2'}>
                        {title}
                      </Text>
                      {description && (
                        <MediaQuery
                          smallerThan="sm"
                          styles={{ display: 'none' }}
                        >
                          <Text size="p2" as={'span'}>
                            <span
                              dangerouslySetInnerHTML={{ __html: description }}
                            ></span>
                          </Text>
                        </MediaQuery>
                      )}
                      <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
                        <Box>
                          {ctaText && ctaUrl && (
                            <a
                              href={ctaUrl}
                              target="_blank"
                              className="banner_button"
                            >
                              <Button size="md" color="primary">
                                {ctaText}
                              </Button>
                            </a>
                          )}
                          {!isMobile &&
                            type?.toLowerCase() === 'care' &&
                            CARE_LOCALES.includes(locale) && (
                              <BookingSearchBar
                                key={String(isMobile)}
                                isMobile={isMobile}
                                careApiDomain={
                                  ENV.NEXT_PUBLIC_CARE_API ||
                                  'https://service-health-care.hellohealthgroup.com/'
                                }
                                careFrontDomain="/care"
                                geoLocationPermission="denied"
                                gaTrackingSearchBar={{
                                  dataEventAction: 'Search Click',
                                  dataEventCategory: 'Care',
                                  dataEventLabel: `https://${domainLocales[locale]}${basePath}/`,
                                }}
                              />
                            )}
                        </Box>
                      </MediaQuery>
                    </Stack>

                    {!!image && (
                      <StyledHomepageImg>
                        <NextImageWrap
                          src={image}
                          className="home-banner-image"
                          alt=""
                          layout="fill"
                          priority
                          placeholder={undefined}
                          blurDataURL={undefined}
                        />
                      </StyledHomepageImg>
                    )}
                  </Flex>
                  <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
                    <Box>
                      {ctaText && ctaUrl && (
                        <a
                          href={ctaUrl}
                          target="_blank"
                          className="banner_button"
                        >
                          <Button size="md" fullWidth>
                            {ctaText}
                          </Button>
                        </a>
                      )}
                      {isMobile &&
                        type?.toLowerCase() === 'care' &&
                        CARE_LOCALES.includes(locale) && (
                          <BookingSearchBar
                            key={String(isMobile)}
                            isMobile={isMobile}
                            careApiDomain={
                              ENV.NEXT_PUBLIC_CARE_API ||
                              'https://service-health-care.hellohealthgroup.com/'
                            }
                            careFrontDomain="/care"
                            geoLocationPermission="denied"
                            gaTrackingSearchBar={{
                              dataEventAction: 'Search Click',
                              dataEventCategory: 'Care',
                              dataEventLabel: `https://${domainLocales[locale]}${basePath}/`,
                            }}
                          />
                        )}
                    </Box>
                  </MediaQuery>
                </Container>
              </StyledHomepageContainer>
            )}
            {!isMobile && <HomepageBannerMenu hasDivider />}
          </>
        ) : (
          <StyledHomepageContainer className="only-menu">
            <HomepageBannerMenu />
          </StyledHomepageContainer>
        )}
      </StyledHomepageBannerWrapper>
      {(title || type === 'upload') && isMobile && (
        <HomepageBannerMenu hasDivider />
      )}
    </>
  )
}

export default HomepageBanner
